import React, { useRef, useState } from "react";
import { RotateCcw, Download, Trash2 } from "lucide-react";
import { WARLI_FIGURES } from "../../data/warli";
import ShareArtwork from "./ShareArtwork";

const CANVAS_W = 800;
const CANVAS_H = 540;
const FIGURE_SIZE = 80; // default size on canvas

const BG_COLORS = [
  { name: "Mud wall", value: "#7B3F1D" },
  { name: "Sunset clay", value: "#A0552A" },
  { name: "Cool earth", value: "#5E3A24" },
];

const FIGURE_COLOR = "#F9F6F0"; // white rice flour

export default function WarliActivity({ onSave }) {
  const canvasRef = useRef(null);
  const [bg, setBg] = useState(BG_COLORS[0].value);
  const [placed, setPlaced] = useState([]); // [{id, figureId, x, y, size}]
  const dragRef = useRef(null);

  const addFigure = (figureId) => {
    const count = placed.length;
    // Stagger across the canvas so new figures don't pile up at the center.
    const cols = 5;
    const col = count % cols;
    const row = Math.floor(count / cols);
    const offsetX = ((col - (cols - 1) / 2) * 130) + (Math.random() * 20 - 10);
    const offsetY = ((row % 3) - 1) * 110 + (Math.random() * 16 - 8);
    const newItem = {
      id: `${figureId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      figureId,
      x: CANVAS_W / 2 + offsetX,
      y: CANVAS_H / 2 + offsetY,
      size: FIGURE_SIZE,
    };
    setPlaced((p) => [...p, newItem]);
  };

  const removeAll = () => setPlaced([]);

  const removeOne = (id) => setPlaced((p) => p.filter((it) => it.id !== id));

  const getPoint = (e) => {
    const svg = canvasRef.current;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const startDrag = (e, id) => {
    e.stopPropagation();
    const item = placed.find((p) => p.id === id);
    if (!item) return;
    const point = getPoint(e);
    dragRef.current = { id, offsetX: item.x - point.x, offsetY: item.y - point.y };
  };

  const onMove = (e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const point = getPoint(e);
    setPlaced((prev) =>
      prev.map((it) =>
        it.id === dragRef.current.id
          ? {
              ...it,
              x: Math.max(40, Math.min(CANVAS_W - 40, point.x + dragRef.current.offsetX)),
              y: Math.max(40, Math.min(CANVAS_H - 40, point.y + dragRef.current.offsetY)),
            }
          : it
      )
    );
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const downloadArt = async () => {
    const svg = canvasRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const out = document.createElement("canvas");
      out.width = CANVAS_W * 2;
      out.height = CANVAS_H * 2;
      const ctx = out.getContext("2d");
      ctx.drawImage(img, 0, 0, out.width, out.height);
      const link = document.createElement("a");
      link.download = "my-warli-village.png";
      link.href = out.toDataURL("image/png");
      link.click();
      if (onSave) onSave();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="warli-activity">
      {/* Canvas */}
      <div className="lg:col-span-9">
        <div
          className="relative w-full rounded-2xl overflow-hidden border-4 border-[#E5DFD3] shadow-xl"
          style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
        >
          <svg
            ref={canvasRef}
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full touch-none"
            onMouseMove={onMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchMove={onMove}
            onTouchEnd={endDrag}
            data-testid="warli-canvas"
          >
            <rect width="100%" height="100%" fill={bg} />
            {/* Subtle ground line */}
            <line
              x1="0"
              y1={CANVAS_H - 30}
              x2={CANVAS_W}
              y2={CANVAS_H - 30}
              stroke={FIGURE_COLOR}
              strokeWidth="2"
              opacity="0.35"
              strokeDasharray="6 8"
            />
            {placed.map((item) => {
              const fig = WARLI_FIGURES.find((f) => f.id === item.figureId);
              if (!fig) return null;
              return (
                <g
                  key={item.id}
                  transform={`translate(${item.x - item.size / 2}, ${item.y - item.size / 2})`}
                  data-testid={`warli-placed-${item.figureId}`}
                  style={{ cursor: "grab" }}
                  onMouseDown={(e) => startDrag(e, item.id)}
                  onTouchStart={(e) => startDrag(e, item.id)}
                >
                  <g transform={`scale(${item.size / 100})`} color={FIGURE_COLOR}>
                    {fig.svg}
                  </g>
                  <rect
                    width={item.size}
                    height={item.size}
                    fill="transparent"
                    onDoubleClick={() => removeOne(item.id)}
                  />
                </g>
              );
            })}
            {placed.length === 0 && (
              <text
                x={CANVAS_W / 2}
                y={CANVAS_H / 2}
                textAnchor="middle"
                fill={FIGURE_COLOR}
                opacity="0.5"
                fontFamily="Outfit, sans-serif"
                fontSize="20"
              >
                Tap a figure on the right to add it to your village →
              </text>
            )}
          </svg>
        </div>
        <p className="mt-4 text-sm text-[#5C6A8F] text-center">
          Tap a figure to add it · drag to move · double-tap a figure to remove it
        </p>
      </div>

      {/* Tools */}
      <div className="lg:col-span-3 space-y-6">
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Figures</p>
          <div className="grid grid-cols-4 lg:grid-cols-2 gap-2">
            {WARLI_FIGURES.map((f) => (
              <button
                key={f.id}
                data-testid={`warli-figure-${f.id}`}
                onClick={() => addFigure(f.id)}
                aria-label={`Add ${f.name}`}
                title={f.name}
                className="aspect-square rounded-lg bg-[#7B3F1D] hover:bg-[#5E3A24] border border-[#E5DFD3] flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
              >
                <svg viewBox="0 0 100 100" className="w-3/4 h-3/4" color={FIGURE_COLOR}>
                  {f.svg}
                </svg>
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Wall colour</p>
          <div className="flex gap-2">
            {BG_COLORS.map((c) => (
              <button
                key={c.value}
                data-testid={`warli-bg-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setBg(c.value)}
                aria-label={c.name}
                style={{ background: c.value }}
                className={`w-10 h-10 rounded-full border-2 transition-transform ${
                  bg === c.value
                    ? "scale-110 border-[#1E2A4F] ring-2 ring-[#1E2A4F]/30 ring-offset-2 ring-offset-[#F9F6F0]"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        <button
          data-testid="warli-clear"
          onClick={removeAll}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
        >
          <Trash2 size={14} /> Clear village
        </button>

        <button
          data-testid="warli-download"
          onClick={downloadArt}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#7B3F1D] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
        >
          <Download size={16} /> Save my village
        </button>
        <ShareArtwork
          artFormId="warli"
          artName="Warli village"
          accent="#7B3F1D"
          testIdPrefix="warli"
        />
      </div>
    </div>
  );
}
